/**
 * Skeleton — golden shimmer placeholder used across the app while data loads.
 * Keeps layout stable (no jank) and hints at the shape of what's coming.
 */
export function Skeleton({ className = "", rounded = "rounded-xl" }) {
  return (
    <div
      data-testid="skeleton"
      aria-hidden
      className={`relative overflow-hidden bg-[rgba(255,255,255,0.03)] ${rounded} ${className}`}
    >
      <div className="absolute inset-0 shimmer pointer-events-none" />
    </div>
  );
}

export function SkeletonText({ w = "w-24", h = "h-4" }) {
  return <Skeleton className={`${w} ${h}`} rounded="rounded-md" />;
}

/**
 * DashboardSkeleton — mirrors the real Dashboard layout so the transition to
 * loaded state feels seamless.  Uses the site-wide `.shimmer` gold gradient.
 */
export function DashboardSkeleton() {
  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6" data-testid="dashboard-skeleton">
      {/* Header row */}
      <div className="flex items-end justify-between mb-2">
        <div className="space-y-2">
          <SkeletonText w="w-24" h="h-3" />
          <SkeletonText w="w-48" h="h-9" />
        </div>
        <div className="text-right space-y-2">
          <SkeletonText w="w-20 ml-auto" h="h-3" />
          <SkeletonText w="w-36 ml-auto" h="h-8" />
        </div>
      </div>

      {/* Top row: Health Score + Next Best Move */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass-card p-8 space-y-6">
          <div className="space-y-3">
            <SkeletonText w="w-32" h="h-3" />
            <SkeletonText w="w-40" h="h-14" />
          </div>
          <div className="space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <SkeletonText w="w-32" h="h-3" />
                <Skeleton className="flex-1 h-2" rounded="rounded-full" />
                <SkeletonText w="w-8" h="h-3" />
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card p-8 border-[rgba(224,106,59,0.35)] space-y-5">
          <Skeleton className="w-40 h-6" rounded="rounded-full" />
          <SkeletonText w="w-3/4" h="h-8" />
          <div className="space-y-2">
            <SkeletonText w="w-full" h="h-3" />
            <SkeletonText w="w-5/6" h="h-3" />
            <SkeletonText w="w-4/6" h="h-3" />
          </div>
          <Skeleton className="w-32 h-11" rounded="rounded-full" />
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="glass-card p-5 space-y-3">
            <SkeletonText w="w-24" h="h-3" />
            <SkeletonText w="w-32" h="h-7" />
          </div>
        ))}
      </div>

      {/* Bottom row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {[0, 1, 2].map((i) => (
          <div key={i} className="glass-card p-6 space-y-4">
            <SkeletonText w="w-40" h="h-4" />
            <div className="space-y-3">
              {[0, 1, 2].map((j) => (
                <Skeleton key={j} className="h-14" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
